import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../lib/apiClient.js';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { FiUser, FiLock, FiGlobe, FiKey, FiCamera, FiCopy, FiMail, FiPhone, FiInfo } from 'react-icons/fi';

const ModernInput = ({ label, icon: Icon, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
    <div className="relative flex items-center">
      {Icon && <Icon className="absolute left-3.5 h-4 w-4 text-slate-400" />}
      <input
        {...props}
        className={`w-full h-11 rounded-xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-slate-400 focus:bg-white focus:ring-4 focus:ring-slate-100 placeholder:text-slate-400 placeholder:font-medium ${Icon ? 'pl-10' : ''} ${props.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      />
    </div>
  </label>
);

export default function Settings() {
  const { profile, reloadProfile } = useAuth();
  return (
    <div className="animate-dc-rise mx-auto max-w-5xl space-y-8 pb-12">
      <div className="px-2">
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Settings</h1>
        <p className="mt-2 text-sm font-semibold text-slate-500">Manage your profile, security, and integration preferences.</p>
      </div>
      
      <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
        <div className="space-y-8">
          <ProfileCard profile={profile} onSaved={reloadProfile} />
        </div>
        <div className="space-y-8">
          <PasswordCard email={profile?.email} />
          <DomainCard />
          <ApiTokensCard />
        </div>
      </div>
    </div>
  );
}

function PasswordCard({ email }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setMsg({ type: '', text: '' }); };

  const update = useMutation({
    mutationFn: async () => {
      if (form.next.length < 8) throw new Error('New password must be at least 8 characters');
      if (form.next !== form.confirm) throw new Error('New passwords do not match');
      if (!email) throw new Error('Missing account email');
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password: form.current });
      if (signInErr) throw new Error('Current password is incorrect');
      const { error } = await supabase.auth.updateUser({ password: form.next });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setForm({ current: '', next: '', confirm: '' }); setMsg({ type: 'ok', text: 'Password updated successfully' }); },
    onError: (err) => setMsg({ type: 'err', text: err.message }),
  });

  const canSubmit = form.current && form.next && form.confirm && !update.isPending;

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
          <FiLock className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-900">Security</h2>
          <p className="text-xs font-semibold text-slate-500">Update your account password.</p>
        </div>
      </div>
      <div className="grid gap-5">
        <ModernInput type="password" label="Current password" icon={FiLock} value={form.current} onChange={set('current')} />
        <ModernInput type="password" label="New password" icon={FiLock} placeholder="At least 8 characters" value={form.next} onChange={set('next')} />
        <ModernInput type="password" label="Confirm new password" icon={FiLock} value={form.confirm} onChange={set('confirm')} />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-6">
        {msg.text ? (
           <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${msg.type === 'err' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</span>
        ) : <div />}
        <button onClick={() => update.mutate()} disabled={!canSubmit} className="flex h-11 items-center justify-center rounded-full bg-slate-900 px-6 text-sm font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
          {update.isPending ? <Spinner className="w-5 h-5 text-white" /> : 'Update Password'}
        </button>
      </div>
    </div>
  );
}

const TZ_LIST = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];

function ProfileCard({ profile, onSaved }) {
  const [form, setForm] = useState({
    name: profile?.name || '',
    username: profile?.username || '',
    brand_name: profile?.brand_name || '',
    phone: profile?.phone || '',
    timezone: profile?.timezone || 'Asia/Kolkata',
    locale: profile?.locale || 'en',
  });
  const [msg, setMsg] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => api.updateMe(form),
    onSuccess: async () => { setMsg('Profile saved successfully'); setTimeout(() => setMsg(''), 3000); await onSaved?.(); },
    onError: (err) => setMsg(err.message),
  });

  const [uploading, setUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState('');
  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setAvatarMsg('');
    try { await api.uploadAvatar(file); await onSaved?.(); setAvatarMsg('Logo updated'); setTimeout(() => setAvatarMsg(''), 3000); }
    catch (err) { setAvatarMsg(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const tzOptions = TZ_LIST.includes(form.timezone) || !form.timezone ? TZ_LIST : [form.timezone, ...TZ_LIST];

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow">
      <div className="mb-8 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
          <FiUser className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-900">Profile &amp; Branding</h2>
          <p className="text-xs font-semibold text-slate-500">Manage account details and company logo.</p>
        </div>
      </div>

      <div className="mb-8 flex items-center gap-5 rounded-2xl bg-slate-50 border border-slate-100 p-5">
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt="Brand logo" className="h-16 w-16 rounded-xl object-cover shadow-sm ring-1 ring-slate-200" />
        ) : (
          <div className="grid h-16 w-16 place-items-center rounded-xl bg-gradient-to-br from-slate-200 to-slate-300 text-xl font-bold text-slate-600 shadow-sm ring-1 ring-slate-200">
            {(profile?.name || profile?.username || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <label className="inline-flex cursor-pointer items-center gap-2 group relative overflow-hidden rounded-xl bg-white border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-400">
            <FiCamera className="h-4 w-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
            <span>Upload Logo</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickFile} disabled={uploading} />
            {uploading && <Spinner className="w-4 h-4 text-indigo-600" />}
          </label>
          {avatarMsg && <p className="mt-2 text-[11px] font-bold text-emerald-600">{avatarMsg}</p>}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <ModernInput label="Full name" icon={FiUser} value={form.name} onChange={set('name')} />

        <label className="block">
          <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600">Username (booking link)</span>
          <div className="flex items-center rounded-xl border border-slate-300 bg-slate-50 focus-within:border-slate-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-slate-100 transition-all overflow-hidden h-11">
            <span className="pl-4 pr-1 text-sm font-bold text-slate-400">/u/</span>
            <input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase() }))}
              className="w-full bg-transparent px-1 py-2 text-sm font-semibold text-slate-800 outline-none"
            />
          </div>
        </label>

        <ModernInput label="Email address" icon={FiMail} value={profile?.email || ''} disabled />
        <ModernInput label="Company / brand name" icon={FiInfo} value={form.brand_name} onChange={set('brand_name')} />
        <ModernInput label="Phone number" icon={FiPhone} placeholder="+1 (555) 000-0000" value={form.phone} onChange={set('phone')} />

        <label className="block">
          <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600">Timezone</span>
          {tzOptions.length ? (
            <select value={form.timezone} onChange={set('timezone')} className="w-full h-11 rounded-xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-slate-400 focus:bg-white focus:ring-4 focus:ring-slate-100 cursor-pointer">
              {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          ) : (
            <ModernInput value={form.timezone} onChange={set('timezone')} />
          )}
        </label>

        <label className="block sm:col-span-2">
          <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600">Locale</span>
          <select value={form.locale} onChange={set('locale')} className="w-full h-11 rounded-xl border border-slate-300 bg-slate-50 px-4 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-slate-400 focus:bg-white focus:ring-4 focus:ring-slate-100 cursor-pointer">
            <option value="en">English (US)</option>
            <option value="bn">Bengali (বাংলা)</option>
          </select>
        </label>
      </div>

      <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
        {msg ? <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">{msg}</span> : <div/>}
        <button onClick={() => save.mutate()} disabled={save.isPending} className="flex h-11 items-center justify-center rounded-full bg-slate-900 px-8 text-sm font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50">
          {save.isPending ? <Spinner className="w-5 h-5 text-white" /> : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}

function DomainCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['domain'], queryFn: api.getDomain, retry: false });
  const [domain, setDomain] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['domain'] });
  const set = useMutation({ mutationFn: () => api.setDomain(domain), onSuccess: invalidate });
  const verify = useMutation({ mutationFn: api.verifyDomain, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: api.removeDomain, onSuccess: invalidate });

  if (isLoading) return <PageSkeleton />;
  if (error) return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 border border-slate-100">
          <FiGlobe className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-900">Custom Domain</h2>
            <Badge tone="slate" className="font-bold shadow-sm">LOCKED</Badge>
          </div>
          <p className="text-xs font-semibold text-slate-500">Host your booking page on your own domain.</p>
        </div>
      </div>
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
        <FiGlobe className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <h3 className="text-sm font-bold text-slate-900">Available on Custom Plan</h3>
        <p className="mt-1 text-xs font-semibold text-slate-500 mx-auto max-w-xs">Upgrade your account to unlock custom domains and remove DayCal branding.</p>
        <Link to="/upgrade" className="mt-5 inline-flex h-9 items-center justify-center rounded-full bg-slate-900 px-5 text-xs font-bold text-white transition-all hover:bg-slate-800">
          Upgrade Plan
        </Link>
      </div>
    </div>
  );

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-50 text-purple-600 border border-purple-100">
          <FiGlobe className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-900">Custom Domain</h2>
            <Badge tone="brand" className="font-bold shadow-sm">PRO</Badge>
          </div>
          <p className="text-xs font-semibold text-slate-500">Host your booking page on your own domain.</p>
        </div>
      </div>
      
      {data?.custom_domain ? (
        <div className="grid gap-4">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
            <span className="font-mono text-sm font-bold text-slate-800">{data.custom_domain}</span>
            {data.verified ? <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-800">Verified</span> : <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-amber-800">Pending</span>}
          </div>
          {!data.verified && data.dns_record && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
              <div className="mb-2 text-xs font-bold text-blue-900">Add this DNS record to verify:</div>
              <div className="font-mono text-[11px] font-semibold text-blue-800 bg-white rounded-lg border border-blue-100 px-3 py-2">
                {data.dns_record.type} {data.dns_record.host} = {data.dns_record.value}
              </div>
            </div>
          )}
          <div className="mt-2 flex gap-3">
            {!data.verified && (
              <button onClick={() => verify.mutate()} disabled={verify.isPending} className="flex h-10 items-center justify-center rounded-full bg-slate-900 px-5 text-xs font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50">
                {verify.isPending ? <Spinner className="w-4 h-4" /> : 'Verify Now'}
              </button>
            )}
            <button onClick={() => remove.mutate()} className="flex h-10 items-center justify-center rounded-full bg-red-50 text-red-600 px-5 text-xs font-bold transition-all hover:bg-red-100 hover:text-red-700">
              Remove
            </button>
          </div>
          {verify.isError && <p className="text-xs font-bold text-amber-600">{verify.error.message}</p>}
        </div>
      ) : (
        <div>
          <ModernInput label="Domain Name" placeholder="book.yoursite.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
          <div className="mt-4 flex justify-end">
            <button onClick={() => set.mutate()} disabled={set.isPending || !domain} className="flex h-11 items-center justify-center rounded-full bg-slate-900 px-6 text-sm font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50">
              {set.isPending ? <Spinner className="w-5 h-5" /> : 'Add Domain'}
            </button>
          </div>
        </div>
      )}
      {set.isError && <p className="mt-3 text-xs font-bold text-red-600">{set.error.message}</p>}
    </div>
  );
}

function ApiTokensCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['api-tokens'], queryFn: api.listApiTokens, retry: false });
  const [name, setName] = useState('');
  const [newToken, setNewToken] = useState('');
  const create = useMutation({
    mutationFn: () => api.createApiToken(name),
    onSuccess: (res) => { setNewToken(res.token); setName(''); qc.invalidateQueries({ queryKey: ['api-tokens'] }); },
  });
  const revoke = useMutation({ mutationFn: (id) => api.revokeApiToken(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens'] }) });

  if (isLoading) return <PageSkeleton />;
  if (error) return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 border border-slate-100">
          <FiKey className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-900">API Tokens</h2>
            <Badge tone="slate" className="font-bold shadow-sm">LOCKED</Badge>
          </div>
          <p className="text-xs font-semibold text-slate-500">Manage programmatic access keys.</p>
        </div>
      </div>
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
        <FiKey className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <h3 className="text-sm font-bold text-slate-900">Available on Custom Plan</h3>
        <p className="mt-1 text-xs font-semibold text-slate-500 mx-auto max-w-xs">Upgrade your account to generate API tokens for programmatic integrations.</p>
        <Link to="/upgrade" className="mt-5 inline-flex h-9 items-center justify-center rounded-full bg-slate-900 px-5 text-xs font-bold text-white transition-all hover:bg-slate-800">
          Upgrade Plan
        </Link>
      </div>
    </div>
  );
  const tokens = data?.api_tokens || [];

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-7 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
          <FiKey className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-900">API Tokens</h2>
            <Badge tone="brand" className="font-bold shadow-sm">PRO</Badge>
          </div>
          <p className="text-xs font-semibold text-slate-500">Manage programmatic access keys.</p>
        </div>
      </div>
      
      {newToken && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiInfo className="text-amber-600 h-4 w-4" />
            <span className="text-[11px] font-black uppercase tracking-wider text-amber-800">Secret Token Created</span>
          </div>
          <div className="mb-2 text-xs font-bold text-amber-900">Copy your token now — it won't be shown again!</div>
          <div className="flex items-center justify-between rounded-lg bg-white border border-amber-100 p-2">
            <div className="break-all font-mono text-[11px] text-slate-700 px-2">{newToken}</div>
            <button onClick={() => navigator.clipboard.writeText(newToken)} className="p-1.5 text-slate-400 hover:text-amber-600 transition-colors" title="Copy to clipboard">
              <FiCopy className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      <div className="mb-6 flex flex-col gap-3">
        <ModernInput label="New Token Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="My integration" />
        <div className="flex justify-end">
          <button onClick={() => create.mutate()} disabled={create.isPending || !name} className="flex h-11 items-center justify-center rounded-full bg-slate-900 px-6 text-sm font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50">
            {create.isPending ? <Spinner className="w-5 h-5" /> : 'Create Token'}
          </button>
        </div>
      </div>
      
      <div className="flex flex-col gap-2">
        <h3 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Active Tokens</h3>
        {tokens.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:border-slate-200 hover:bg-slate-100/50">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-bold text-slate-800">{t.name || '(unnamed)'}</span>
              {t.revoked_at && <span className="inline-block rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-red-700 w-fit">Revoked</span>}
            </div>
            {!t.revoked_at && (
              <button onClick={() => revoke.mutate(t.id)} className="rounded-full bg-white border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600 hover:border-red-100 shadow-sm">
                Revoke
              </button>
            )}
          </div>
        ))}
        {tokens.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs font-bold text-slate-400">
            No tokens created yet.
          </div>
        )}
      </div>
    </div>
  );
}
